/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/theme-chrome";
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/ext-spellcheck";
import debounce from "lodash.debounce";
import Typography from "@mui/material/Typography";
import LabelledField from "components/labelled-field";
import { ListItemContext } from "cms/fields/field-lists/field-list-context";
import { isStringNotEmpty, isNotString, generateGuid } from "utils/empty-exists";
import { FormGroup, FormControlLabel, Switch } from "@mui/material";

const editorTheme = "chrome";

const EmbedFieldItemEdit = () => {
    
    const { t } = useTranslation();
    const { index, value, settings, updateListItem } = useContext(ListItemContext);
    const {
        html,
        script,
        isResponsive: isResponsiveValue,
        enableScript: enableScriptValue
    } = value;
    const idhtml = generateGuid();
    const idscript = generateGuid();
    const hasContent = isStringNotEmpty(html);
    const initialHtml = hasContent ? html : "<!-- SimpliCity CMS Embed placeholder -->";
    const initialScript = isStringNotEmpty(script) ? script : "/* <!-- SimpliCity CMS Embed placeholder --> */";
    const initialValue = { html: initialHtml, script: initialScript, isResponsive: isResponsiveValue, enableScript: enableScriptValue };
    const initialLocalState = {
        value: value ? value : initialValue,
        settings
    };
    const [itemLocalState, setItemLocalState] = useState(initialLocalState);
    const [editorState, setEditorState] = useState(initialHtml);
    
    let _isResponsive = isResponsiveValue || false;
    // if (isStringNotEmpty(isResponsiveValue)) {
    //     _isResponsive = isResponsiveValue === 'on'  // should not be a string
    // }
    const [isResponsive, setIsResponsive] = useState(_isResponsive);
    
    let _enableScript = enableScriptValue || false;
    // if (isStringNotEmpty(enableScriptValue)) {
    //     _enableScript = enableScriptValue === 'on'  // should not be a string
    // }
    const [enableScript, setEnableScript] = useState(_enableScript);
    const [editorScriptState, setEditorScriptState] = useState(initialScript);
    
    // useEffect(() => {
    //     if (!editorState) {
    //         // only on load
    //         setEditorState(value)
    //     }
    // }, [editorState])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const update = useCallback(
        debounce((updateState, index) => {
            updateListItem(updateState, index);
        }, 1200),
        [updateListItem]
    );
    const handleEditorChange = (html) => {
        if (isNotString(html)) {
            html = "<!-- SimpliCity CMS Embed placeholder -->\n";
        }
        setEditorState(html);
        const _script = itemLocalState?.value?.script;
        const _isResponsive = itemLocalState?.value?.isResponsive;
        const _enableScript = itemLocalState?.value?.enableScript;
        const value = { html, _script, _isResponsive, _enableScript };
        const updatedValue = { ...itemLocalState, value: value };
        update(updatedValue, index);
        setItemLocalState(updatedValue);
    };
    const handleEditorScriptChange = (script) => {
        if (isNotString(script)) {
            script = "/*<!-- SimpliCity CMS Embed placeholder -->*/\n";
        }
        setEditorScriptState(script);
        const _html = itemLocalState?.value?.html;
        const _isResponsive = itemLocalState?.value?.isResponsive;
        const _enableScript = itemLocalState?.value?.enableScript;
        const value = { _html, script, _isResponsive, _enableScript };
        const nextState = { ...itemLocalState, value };
        update(itemLocalState, nextState);
        setItemLocalState(nextState);
    };
    const onChangeIsResponsive = (event) => {
        const checked = event.target.checked;
        setIsResponsive(checked);
        const _html = itemLocalState?.value?.html;
        const _script = itemLocalState?.value?.script;
        const _enableScript = itemLocalState?.value?.enableScript;
        const value = { _html, _script, isResponsive: checked, _enableScript };
        const nextState = { ...itemLocalState, value };
        update(itemLocalState, nextState);
        setItemLocalState(nextState);
    };
    const onChangeEnableScript = (event) => {
        const checked = event.target.checked;
        setEnableScript(checked);
        const _html = itemLocalState?.value?.html;
        const _script = itemLocalState?.value?.script;
        const _isResponsive = itemLocalState?.value?.script;
        const value = { _html, _script, enableScript: checked, _isResponsive };
        const nextState = { ...itemLocalState, value };
        update(itemLocalState, nextState);
        setItemLocalState(nextState);
    };
    
    return (
        <Fragment>
            <div className='simplicity-composer-field-primary-layout'>
                <div className='embed-editor-container'>
                    <div className='simplicity-composer-embed'>
                        
                        <LabelledField
                            label={t("commons.embedHTMLCode")}
                            className='labelField'
                        >
                            <div style={{ border: "1px solid black" }}>
                                {editorState &&
                                    <AceEditor
                                        value={editorState}
                                        mode='html'
                                        theme={editorTheme}
                                        onChange={handleEditorChange}
                                        name={`simplicity-json-editor-${idhtml}`}
                                        editorProps={{ $blockScrolling: true }}
                                        setOptions={{
                                            useWorker: false,
                                            enableBasicAutocompletion: true,
                                            enableLiveAutocompletion: true,
                                            enableSnippets: true,
                                            showPrintMargin: false,
                                        }}
                                        width='100%'
                                        tabsize='2'
                                    />
                                }
                            </div>
                        </LabelledField>
                        
                        <Typography variant='overline' color={"text.secondary"}>
                            Make IFrame Responsive (16:9):
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isResponsive}
                                        onChange={onChangeIsResponsive}
                                    />
                                }
                                label='Enabled'
                            />
                        </FormGroup>
                        
                        <Typography variant='overline' color={"text.secondary"}>
                            Script Code:
                        </Typography>
                        {enableScript &&
                            <div style={{ border: "1px solid black" }}>
                                <AceEditor
                                    value={editorScriptState}
                                    mode='html'
                                    theme={editorTheme}
                                    onChange={handleEditorScriptChange}
                                    name={`simplicity-json-editor-${idscript}`}
                                    editorProps={{ $blockScrolling: true }}
                                    setOptions={{
                                        useWorker: false,
                                        enableBasicAutocompletion: false,
                                        enableLiveAutocompletion: false,
                                        enableSnippets: false,
                                        showPrintMargin: false,
                                    }}
                                    width='100%'
                                    tabsize='2'
                                />
                            </div>
                        }
                        <Typography variant='overline' color={"text.secondary"}>
                            Enable Script:
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={enableScript}
                                        onChange={onChangeEnableScript}
                                    />
                                }
                                label='Enabled'
                            />
                        </FormGroup>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};
export default EmbedFieldItemEdit;
